import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Stack,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { Add as AddIcon, Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material'
import type { ColumnDefinition } from '@/types/database'

type ColumnType = 'text' | 'number' | 'date'

interface ColumnManagerProps {
  open: boolean
  onClose: () => void
  columns: ColumnDefinition[]
  onCreateColumn: (name: string, type: ColumnType) => Promise<void>
  onDeleteColumn: (id: string) => Promise<void>
}

export function ColumnManager({
  open,
  onClose,
  columns,
  onCreateColumn,
  onDeleteColumn,
}: ColumnManagerProps) {
  const [showAdd, setShowAdd] = useState(false)
  const [newColumnName, setNewColumnName] = useState('')
  const [newColumnType, setNewColumnType] = useState<ColumnType>('text')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setShowAdd(false)
      setNewColumnName('')
      setNewColumnType('text')
      setError(null)
      setIsSaving(false)
      setDeletingId(null)
    }
  }, [open])

  const handleAddColumn = useCallback(async () => {
    const trimmedName = newColumnName.trim()
    if (!trimmedName) {
      setError('Column name is required')
      return
    }

    setError(null)
    setIsSaving(true)
    try {
      await onCreateColumn(trimmedName, newColumnType)
      setNewColumnName('')
      setNewColumnType('text')
      setShowAdd(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create column')
    } finally {
      setIsSaving(false)
    }
  }, [newColumnName, newColumnType, onCreateColumn])

  const handleDeleteColumn = useCallback(async (id: string) => {
    setError(null)
    setDeletingId(id)
    try {
      await onDeleteColumn(id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete column')
    } finally {
      setDeletingId(null)
    }
  }, [onDeleteColumn])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Custom Columns</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}

          {showAdd ? (
            <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Add New Column
              </Typography>
              <Stack direction="row" spacing={2} alignItems="flex-end">
                <TextField
                  label="Column Name"
                  value={newColumnName}
                  onChange={(event) => setNewColumnName(event.target.value)}
                  size="small"
                  sx={{ flexGrow: 1 }}
                  autoFocus
                  disabled={isSaving}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={newColumnType}
                    onChange={(event) => setNewColumnType(event.target.value as ColumnType)}
                    label="Type"
                    disabled={isSaving}
                  >
                    <MenuItem value="text">Text</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  onClick={() => void handleAddColumn()}
                  variant="contained"
                  size="small"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Add'}
                </Button>
                <Button onClick={() => setShowAdd(false)} size="small" disabled={isSaving}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          ) : (
            <Button
              startIcon={<AddIcon />}
              onClick={() => setShowAdd(true)}
              variant="outlined"
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Column
            </Button>
          )}

          <Divider />

          <Typography variant="subtitle2">Configured Columns</Typography>
          {columns.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No custom columns configured yet.
            </Typography>
          ) : (
            <List dense disablePadding>
              {columns.map((column) => (
                <ListItem
                  key={column.id}
                  divider
                  disableGutters
                  secondaryAction={(
                    <IconButton
                      edge="end"
                      color="error"
                      size="small"
                      onClick={() => void handleDeleteColumn(column.id)}
                      disabled={deletingId === column.id}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                >
                  <ListItemText
                    primary={column.name}
                    secondary={(
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip size="small" label={column.type} />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(column.createdAt).toLocaleString('de-DE')}
                        </Typography>
                      </Stack>
                    )}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
